/**
 * Orthographic "sensor bubble" — the tactical-map projection used by both
 * the system map (minimap + chart SYSTEM tab) and the galactic chart's
 * GALAXY tab. A tilted orthographic view of a plane: positions project
 * with a yaw spin and a fixed-ish tilt, height rises off the plane as a
 * STEM with a base ring (the Homeworld/Elite answer to 3D on a 2D map).
 *
 * Pure canvas 2D — a few dozen arcs and lines per redraw, no scene graph,
 * no per-frame allocation beyond the node list the caller assembles.
 */
export interface SensorNode {
  x: number;
  y: number;
  z: number;
  color: string;
  /** Icon radius in px. */
  size: number;
  shape?: "dot" | "diamond" | "arrow" | "ringdot";
  label?: string;
  /** Secondary line under the label (distance etc.). */
  sub?: string;
  /** Heading (rad, XZ) for the arrow shape. */
  heading?: number;
  /** Draw an orbit ring through this node's plane radius. */
  ring?: boolean;
  ringDash?: boolean;
  dim?: boolean;
  /** Waypoint highlight ring around this node. */
  selected?: boolean;
  /** Hit-testing id (clickable charts). */
  id?: string;
}

export interface SensorOpts {
  labels: boolean;
  /** sqrt radial compression (system scale); galaxy lattice stays linear. */
  sqrtScale: boolean;
  /** Concentric grid ellipses (system map) — count, or 0 for none. */
  gridRings: number;
}

interface Projected {
  sx: number;
  sy: number;
  depth: number;
  id?: string;
}

export class SensorView {
  yaw = 0.7;
  tilt = 1.0; // rad from horizontal: ~57° down-angle
  /** View zoom (wheel) and screen-space pan — drive the intro zoom-out. */
  zoom = 1;
  panX = 0;
  panY = 0;
  private hits: Projected[] = [];
  private dragOn = false;
  private lastX = 0;
  private lastY = 0;
  private introT = -1;
  private introFrom: { zoom: number; panX: number; panY: number } | null = null;

  /**
   * "Zoom out of where you are": start centered tight on the focus point
   * and ease out to the full view. Pass the focus node's RAW projected
   * offset from canvas center (lastProjRaw after a first draw).
   */
  introZoomOut(focusOffX: number, focusOffY: number, startZoom = 4): void {
    this.introFrom = { zoom: startZoom, panX: focusOffX, panY: focusOffY };
    this.introT = 0;
  }

  /** Advance the intro ease; true while animating (caller keeps redrawing). */
  tickIntro(dt: number): boolean {
    if (this.introT < 0 || !this.introFrom) return false;
    this.introT = Math.min(1, this.introT + dt * 1.6);
    const k = 1 - Math.pow(1 - this.introT, 3); // ease-out
    this.zoom = this.introFrom.zoom + (1 - this.introFrom.zoom) * k;
    this.panX = this.introFrom.panX * (1 - k);
    this.panY = this.introFrom.panY * (1 - k);
    if (this.introT >= 1) {
      this.introT = -1;
      this.introFrom = null;
      return false;
    }
    return true;
  }

  /** Raw projector from the last draw (intro-anim focus offsets). */
  lastProjRaw: ((x: number, y: number, z: number) => [number, number]) | null = null;

  /** Drag-rotate (chart views). Returns an unbind function. */
  attach(canvas: HTMLCanvasElement, redraw: () => void): () => void {
    const down = (e: PointerEvent): void => {
      this.dragOn = true;
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const move = (e: PointerEvent): void => {
      if (!this.dragOn) return;
      // Grab-the-world semantics: the map follows the hand.
      this.yaw -= (e.clientX - this.lastX) * 0.008;
      this.tilt = Math.max(0.25, Math.min(1.45, this.tilt + (e.clientY - this.lastY) * 0.006));
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      redraw();
    };
    const wheel = (e: WheelEvent): void => {
      e.preventDefault();
      const dy = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      this.zoom = Math.max(0.5, Math.min(9, this.zoom * Math.pow(1.0015, -dy)));
      redraw();
    };
    const up = (): void => {
      this.dragOn = false;
    };
    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", up);
    canvas.addEventListener("pointercancel", up);
    canvas.addEventListener("wheel", wheel, { passive: false });
    return () => {
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", up);
      canvas.removeEventListener("pointercancel", up);
      canvas.removeEventListener("wheel", wheel);
    };
  }

  /** Node id within 16px of a canvas-space point, or null. */
  hit(x: number, y: number): string | null {
    let best: string | null = null;
    let bestD = 16;
    for (const p of this.hits) {
      if (!p.id) continue;
      const d = Math.hypot(p.sx - x, p.sy - y);
      if (d < bestD) {
        bestD = d;
        best = p.id;
      }
    }
    return best;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    nodes: SensorNode[],
    opts: SensorOpts,
  ): void {
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;
    const margin = opts.labels ? 34 : 10;
    const R = Math.min(w, h) / 2 - margin;
    const sinT = Math.sin(this.tilt);
    const cosT = Math.cos(this.tilt);
    const cosY = Math.cos(this.yaw);
    const sinY = Math.sin(this.yaw);

    let rMax = 1;
    for (const n of nodes) rMax = Math.max(rMax, Math.hypot(n.x, n.z), Math.abs(n.y) * 1.4);
    const radial = (r: number): number =>
      opts.sqrtScale ? Math.sqrt(r / rMax) * R : (r / rMax) * R;

    // Project: yaw-rotate XZ, compress plane by sinT, lift by Y·cosT.
    const zm = this.zoom;
    const vx = (sx: number): number => (sx - cx - this.panX) * zm + cx;
    const vy = (sy: number): number => (sy - cy - this.panY) * zm + cy;
    const rawProj = (x: number, y: number, z: number): [number, number, number] => {
      const r = Math.hypot(x, z);
      const k = r > 1e-9 ? radial(r) / r : 0;
      const X = (x * cosY - z * sinY) * k;
      const Z = (x * sinY + z * cosY) * k;
      const Y = (Math.sign(y) * (opts.sqrtScale ? Math.sqrt(Math.abs(y) / rMax) * R : (Math.abs(y) / rMax) * R)) || 0;
      const lift = Math.max(-R * 0.6, Math.min(R * 0.6, Y));
      return [X, Z * sinT - lift * cosT, Z];
    };
    this.lastProjRaw = (x, y, z) => {
      const [a, b] = rawProj(x, y, z);
      return [a, b];
    };
    const proj = (
      x: number,
      y: number,
      z: number,
    ): { sx: number; syPlane: number; sy: number; depth: number } => {
      const r = Math.hypot(x, z);
      const k = r > 1e-9 ? radial(r) / r : 0;
      const X = (x * cosY - z * sinY) * k;
      const Z = (x * sinY + z * cosY) * k;
      const Y = (Math.sign(y) * (opts.sqrtScale ? Math.sqrt(Math.abs(y) / rMax) * R : (Math.abs(y) / rMax) * R)) || 0;
      const lift = Math.max(-R * 0.6, Math.min(R * 0.6, Y));
      return {
        sx: vx(cx + X),
        syPlane: vy(cy + Z * sinT),
        sy: vy(cy + Z * sinT - lift * cosT),
        depth: Z,
      };
    };

    // Grid: concentric plane ellipses + a horizon cross.
    if (opts.gridRings > 0) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(90, 140, 190, 0.13)";
      for (let i = 1; i <= opts.gridRings; i++) {
        const rr = ((R * i) / opts.gridRings) * zm;
        ctx.beginPath();
        ctx.ellipse(vx(cx), vy(cy), rr, rr * sinT, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(vx(cx) - R * zm, vy(cy));
      ctx.lineTo(vx(cx) + R * zm, vy(cy));
      ctx.strokeStyle = "rgba(90, 140, 190, 0.1)";
      ctx.stroke();
    }

    // Orbit rings (under everything else).
    for (const n of nodes) {
      if (!n.ring) continue;
      const rr = radial(Math.hypot(n.x, n.z)) * zm;
      ctx.strokeStyle = n.ringDash ? "rgba(255, 210, 130, 0.22)" : "rgba(110, 160, 210, 0.2)";
      if (n.ringDash) ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.ellipse(vx(cx), vy(cy), rr, rr * sinT, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Nodes, painter-ordered back-to-front so near icons overlap far ones.
    const order = nodes
      .map((n, i) => ({ n, i, p: proj(n.x, n.y, n.z) }))
      .sort((a, b) => a.p.depth - b.p.depth);
    this.hits = [];
    ctx.font = "9px 'SF Mono', Menlo, monospace";
    ctx.textAlign = "left";
    for (const { n, p } of order) {
      const alpha = n.dim ? 0.45 : 1;
      // Altitude stem + base ring.
      if (Math.abs(p.sy - p.syPlane) > 2.5) {
        ctx.globalAlpha = alpha * 0.5;
        ctx.strokeStyle = n.color;
        ctx.beginPath();
        ctx.moveTo(p.sx, p.syPlane);
        ctx.lineTo(p.sx, p.sy);
        ctx.stroke();
        ctx.globalAlpha = alpha * 0.8;
        ctx.beginPath();
        ctx.arc(p.sx, p.syPlane, 1.6, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = alpha;
      ctx.fillStyle = n.color;
      if (n.shape === "diamond") {
        ctx.beginPath();
        ctx.moveTo(p.sx, p.sy - n.size);
        ctx.lineTo(p.sx + n.size, p.sy);
        ctx.lineTo(p.sx, p.sy + n.size);
        ctx.lineTo(p.sx - n.size, p.sy);
        ctx.closePath();
        ctx.fill();
      } else if (n.shape === "arrow") {
        ctx.save();
        ctx.translate(p.sx, p.sy);
        ctx.rotate((n.heading ?? 0) + this.yaw);
        ctx.beginPath();
        ctx.moveTo(0, -n.size * 1.4);
        ctx.lineTo(n.size, n.size * 1.1);
        ctx.lineTo(0, n.size * 0.55);
        ctx.lineTo(-n.size, n.size * 1.1);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      } else if (n.shape === "ringdot") {
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, n.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = n.color;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, n.size + 2.5, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, n.size, 0, Math.PI * 2);
        ctx.fill();
      }
      if (opts.labels && n.label) {
        ctx.fillStyle = n.dim ? "rgba(150, 175, 200, 0.55)" : "rgba(195, 225, 250, 0.9)";
        ctx.fillText(n.label, p.sx + n.size + 4, p.sy + 3);
        if (n.sub) {
          ctx.fillStyle = "rgba(120, 150, 180, 0.7)";
          ctx.fillText(n.sub, p.sx + n.size + 4, p.sy + 12);
        }
      }
      if (n.selected) {
        ctx.strokeStyle = "#ff9df5";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(p.sx, p.sy, n.size + 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.lineWidth = 1;
      }
      ctx.globalAlpha = 1;
      if (n.id) this.hits.push({ sx: p.sx, sy: p.sy, depth: p.depth, id: n.id });
    }
  }
}
